import { useMemo } from 'react';
import { List } from '@mui/material';
import { OutlineTreeNode } from './OutlineTreeNode';
import type { OutlineNodeDto } from '../../api/queries/outlines';

interface OutlineTreeViewProps {
  nodes: OutlineNodeDto[];
  selectedNodeId: string | null;
  expandedNodeIds: Set<string>;
  searchQuery: string;
  onSelectNode: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onRenameNode: (id: string, name: string) => void;
}

export function OutlineTreeView({
  nodes,
  selectedNodeId,
  expandedNodeIds,
  searchQuery,
  onSelectNode,
  onToggleExpand,
  onRenameNode,
}: OutlineTreeViewProps) {
  const byParent = useMemo(() => {
    const m = new Map<string | null, OutlineNodeDto[]>();
    const filtered = searchQuery
      ? nodes.filter(
          (n) =>
            n.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            n.synopsis?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            n.nodeType?.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : nodes;
    for (const n of filtered) {
      const key = n.parentId ?? null;
      (m.get(key) ?? m.set(key, []).get(key)!).push(n);
    }
    for (const arr of m.values())
      arr.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    return m;
  }, [nodes, searchQuery]);

  const renderNodes = (
    parentId: string | null,
    depth: number,
  ): React.ReactNode => {
    const items = byParent.get(parentId) ?? [];
    if (items.length === 0) return null;

    return items.map((node) => {
      const childNodes = byParent.get(node.id);
      const hasChildren = !!childNodes && childNodes.length > 0;

      return (
        <OutlineTreeNode
          key={node.id}
          node={node}
          depth={depth}
          isExpanded={expandedNodeIds.has(node.id)}
          isSelected={selectedNodeId === node.id}
          hasChildren={hasChildren}
          onToggleExpand={() => onToggleExpand(node.id)}
          onSelect={() => onSelectNode(node.id)}
          onRename={(name) => onRenameNode(node.id, name)}
        >
          {renderNodes(node.id, depth + 1)}
        </OutlineTreeNode>
      );
    });
  };

  return (
    <List dense disablePadding>
      {renderNodes(null, 0)}
    </List>
  );
}
